import React from "react";

import { FormProvider } from "react-hook-form";
import * as yup from "yup";

import { graphql } from "@/gql";
import {
  CreateRecipeInput,
  FoodItemFragment,
  IngredientListItemFragment,
} from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalDialog from "@/hooks/useGlobalDialog";
import { displayError } from "@/utils/errors";
import { maybeCall } from "@/utils/functions";

import RecipeFormContent from "./RecipeFormContent";
import RecipeFormStepper from "./RecipeFormStepper";

interface Props {
  initialValues: RecipeFormValues;
  isInitialValid?: boolean;
  onSubmit?: (input: CreateRecipeInput) => Promise<void>;
  afterSubmit?: () => void;
  onCancel?: () => void;
  children?: (args: {
    renderStepper: () => React.ReactNode;
    renderForm: () => React.ReactNode;
  }) => React.ReactNode;
}

const validationSchema = yup
  .object({
    name: yup.string().label("Name").required("${label} is required"),
    preparationMinutes: yup
      .number()
      .label("Preparation time")
      .presence()
      .required("${label} is required")
      .integer("${label} must be a whole number")
      .min(0, "${label} must not be negative"),
    portions: yup
      .number()
      .label("Portions per recipe")
      .presence()
      .required("${label} is required")
      .min(0),
    description: yup
      .string()
      .label("Description")
      .required("${label} is required"),
    ingredients: yup
      .array(
        yup
          .object({
            id: yup.string().nullable().optional(),
            food: yup.mixed<IngredientListItemFragment["food"]>().required(), // store entire food object for computing macronutrients, not saved
            foodPortion: yup
              .mixed<IngredientListItemFragment["foodPortion"]>() // store entire food object for computing macronutrients, not saved
              .required(),
            amount: yup.number().required(),
            _destroy: yup.boolean().nullable().optional(),
          })
          .defined()
      )
      .defined(),
    instructions: yup
      .array(
        yup
          .object({
            text: yup.string().required(),
          })
          .defined()
      )
      .defined(),
    // metadata for form, not saved
    meta: yup
      .object({
        activeStep: yup.number().defined(),
        completedSteps: yup.object<Record<number, boolean>>().defined(),
      })
      .defined(),
    selectedIngredient: yup
      .object({
        amount: yup.number().nullable().optional(),
        food: yup.mixed<FoodItemFragment>().nullable().optional(),
        measurement: yup
          .mixed<FoodItemFragment["portions"][0]>()
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
  })
  .defined();

export type RecipeFormValues = yup.InferType<typeof validationSchema>;

export const recipeFormFragment = graphql(`
  fragment RecipeForm on Recipe {
    id
    carbohydrateGrams
    fatGrams
    proteinGrams
    name
    description
    numberOfPortions
    preparationMinutes
    ingredients {
      ...IngredientListItem
    }
    instructions
  }
`);

const RecipeForm: React.FC<Props> = (props) => {
  const { alert } = useGlobalDialog();
  const methods = useFormWithYup({
    defaultValues: props.initialValues,
    mode: "all",
    validationSchema,
  });

  async function submit(values: RecipeFormValues) {
    try {
      const input: CreateRecipeInput = {
        attributes: {
          name: values.name,
          preparationMinutes: values.preparationMinutes,
          numberOfPortions: values.portions,
          description: values.description,
          ingredientsAttributes: values.ingredients.map((i) => ({
            id: i.id,
            foodId: i.food.id,
            foodPortionId: i.foodPortion.id,
            amount: i.amount,
            _destroy: i._destroy,
          })),
          instructions: values.instructions.map((i) => i.text),
        },
      };
      await maybeCall(props.onSubmit, input);
      methods.reset();
      maybeCall(props.afterSubmit);
    } catch (e) {
      alert({
        title: "Failed to add recipe",
        message: displayError(e),
      });
    }
  }

  const renderStepper = () => <RecipeFormStepper />;
  const renderForm = () => <RecipeFormContent onCancel={props.onCancel} />;
  const children = maybeCall(props.children, { renderStepper, renderForm });

  return (
    <form onSubmit={methods.handleSubmit(submit)}>
      <FormProvider {...methods}>{children}</FormProvider>
    </form>
  );
};

export default RecipeForm;
